import {
  SearchIcon
} from "@primer/octicons-react";
import { Box } from "@primer/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  FO_BAOCAO_AN_HOC_SINH_API_END_POINT,
  FO_DANG_KY_AN_HOC_SINH_API_END_POINT,
  foDangKyAnHocSinhApi,
} from "../../../api/foDangKyAnHocSinhApi";
import ComboboxDotDangKyAn from "../../../components/combobox-dot-dang-ky-an/ComboboxDotDangKyAn";
import SelectBoxTrangThaiDangHoc, {
  StudentFilterType,
} from "../../../components/selectbox-trangthai-danghoc";
import ComboboxHe from "../../../components/combobox-he/ComboboxHe";
import ComboboxKhoi from "../../../components/combobox-khoi/ComboboxKhoi";
import { ComboboxTruong } from "../../../components/combobox-truong";
import HocSinhShortInfo from "../../../components/hoc-sinh/HocSinhShortInfo";
import Button from "../../../components/ui/button";
import DataTable from "../../../components/ui/data-table";
import { IColumn } from "../../../components/ui/data-table/DataTable";
import { default as MyText, default as Text } from "../../../components/ui/text";
import MyTextInput from "../../../components/ui/text-input";
import TextWithEllipse from "../../../components/ui/text-with-ellipse";
import { useCommonContext } from "../../../contexts/common";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { useDangKyAnHocSinh } from "../../../hooks/useDangKyAnHocSinh";
import { ePageBaseStatus } from "../../../models/ePageBaseStatus";
import { IFoThucDonItemRequest } from "../../../models/request/thuc-don/IFoThucDonItemRequest";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);

const BaoCaoThangPage = () => {
  const { dm_coso_id, nam_hoc, dm_truong_id } = useCommonSelectedHook();
  const { translate, checkAccesiableTo } = useCommonContext();
  const { status } = useDangKyAnHocSinh();

  const [foBaoCaoAns, setFoBaoCaoAns] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterTruongId, setFilterTruongId] = useState<number>(0);
  const [filterKhoiId, setFilterKhoiId] = useState<number>(0);
  const [filterHeId, setFilterHeId] = useState<number>(0);
  const [filterDotDangKyId, setFilterDotDangKyId] = useState<number>(0);
  const [filterStatusType, setFilterStatusType] = useState<StudentFilterType>("dang_hoc");

  const canView = checkAccesiableTo(`${FO_BAOCAO_AN_HOC_SINH_API_END_POINT}/thang`, "POST");

  const [dataLoad, setDataLoad] = useState<IFoThucDonItemRequest>({
    dm_coso_id,
    dm_truong_id,
    from_date: dayjs().startOf("month").format("YYYY-MM-DD"),
    to_date: new Date().toISOString().split("T")[0],
  });

  const getDayColumns = (fromDateStr: string, toDateStr: string): string[] => {
    const from = dayjs(fromDateStr);
    const to = dayjs(toDateStr);
    const endOfMonth = from.endOf("month");
    const validTo = to.isAfter(endOfMonth) ? endOfMonth : to;
    const days: string[] = [];

    let current = from.startOf("day");
    while (current.isSameOrBefore(validTo, "day")) {
      days.push(current.format("DD"));
      current = current.add(1, "day");
    }

    return days;
  };

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) return;
    const newFrom = dayjs(val);
    if (!newFrom.isValid()) return;
    const newFromStr = newFrom.format("YYYY-MM-DD");
    const currentTo = dayjs(dataLoad.to_date);

    if (
      !currentTo.isValid() ||
      currentTo.format("YYYY-MM") !== newFrom.format("YYYY-MM") ||
      currentTo.isBefore(newFrom, "day")
    ) {
      setDataLoad((prev) => ({
        ...prev,
        from_date: newFromStr,
        to_date: newFrom.endOf("month").format("YYYY-MM-DD"),
      }));
    } else {
      setDataLoad((prev) => ({
        ...prev,
        from_date: newFromStr,
      }));
    }
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) return;
    const newTo = dayjs(val);
    if (!newTo.isValid()) return;
    const newToStr = newTo.format("YYYY-MM-DD");
    const currentFrom = dayjs(dataLoad.from_date);

    if (
      !currentFrom.isValid() ||
      newTo.format("YYYY-MM") !== currentFrom.format("YYYY-MM") ||
      newTo.isBefore(currentFrom, "day")
    ) {
      setDataLoad((prev) => ({
        ...prev,
        from_date: newTo.startOf("month").format("YYYY-MM-DD"),
        to_date: newToStr,
      }));
    } else {
      setDataLoad((prev) => ({
        ...prev,
        to_date: newToStr,
      }));
    }
  };

  const handleSearchUserAsync = useCallback(async () => {
    if (!dm_coso_id || !nam_hoc || !dataLoad.from_date || !dataLoad.to_date) return;

    setLoading(true);
    try {
      const [res, resDiemDanh] = await Promise.all([
        foDangKyAnHocSinhApi.select_by_baocao_thang({
          dm_coso_id,
          dm_truong_id: filterTruongId,
          fo_dotdangky_an_id: filterDotDangKyId,
          nam_hoc,
          from_date: dataLoad.from_date,
          to_date: dataLoad.to_date,
          chi_dang_hoc: filterStatusType === "dang_hoc",
          dm_khoi_id: filterKhoiId,
          dm_he_id: filterHeId,
        }),
        foDangKyAnHocSinhApi.select_by_baocao_diemdanh({
          dm_coso_id,
          dm_truong_id: filterTruongId,
          fo_dotdangky_an_id: filterDotDangKyId,
          nam_hoc,
          from_date: dataLoad.from_date,
          to_date: dataLoad.to_date,
          dm_khoi_id: filterKhoiId,
          dm_he_id: filterHeId,
        }),
      ]);

      if (res.is_success) {
        const rawData = res.data || [];
        const diemDanhData = resDiemDanh.data || [];

        const dayRange = getDayColumns(dataLoad.from_date, dataLoad.to_date); // ['01', '02', ...]

        const splitName = ((fullName?: string) => {
          const parts = (fullName || "").trim().split(/\s+/);
          const last = parts.pop() || ""; // tên gọi (cuối cùng)
          const middle = parts.slice(1).join(" "); // tên đệm
          const first = parts[0] || ""; // họ
          return { first, middle, last };
        })

        // Nhóm theo học sinh
        const groupedData: Record<number, any> = {};

        rawData.forEach((record: any) => {
          const studentId = record.ts_hocsinh_id;
          const dateKey = dayjs(record.ngay_dang_ky).format("DD");

          if (!groupedData[studentId]) {
            groupedData[studentId] = {
              ...record,
              trang_thai_hoc_sinh: record.trang_thai_hoc_sinh || record.trang_thai,
              isDuTuyen: record.isDuTuyen ?? (record.trang_thai === "Đang học" || record.trang_thai_hoc_sinh === "Đang học"),
              ngay_nhap_hoc: record.ngay_nhap_hoc,
              cas: 0,
              kas: 0,
              ka: 0,
              di_hoc: 0,
              nghi_phep: 0,
              vang: 0,
              ngay_tinh_phi: 0,
              comboByDay: {},
            };
          }

          const studentRecord = groupedData[studentId];
          studentRecord.comboByDay[dateKey] = record.fo_xacnhanan_combo_id;

          if (record.fo_xacnhanan_combo_id === 1 || record.fo_xacnhanan_combo_id === 2) {
            studentRecord[`ngay_${dateKey}`] = "✓"; // combo có ăn thì đánh dấu
          }

          // ✅ Ghi nhận số lượng theo combo_id
          // switch (record.fo_xacnhanan_combo_id) {
          //   case 1:
          //     studentRecord.cas++;
          //     studentRecord[`ngay_${dateKey}`] = "✓"; // Chỉ combo có ăn mới hiển thị
          //     break;
          //   case 2:
          //     studentRecord.kas++;
          //     studentRecord[`ngay_${dateKey}`] = "✓";
          //     break;
          //   case 3:
          //     studentRecord.ka++;
          //     break;
          // }
        });

        // Gộp dữ liệu xin nghỉ phép
        diemDanhData.forEach((item: any) => {
          const studentId = item.ts_hocsinh_id;
          if (!groupedData[studentId]) return;

          const studentRecord = groupedData[studentId];
          const dayStr = dayjs(item.ngay_diem_danh).format("DD");
          const key = `ngay_${dayStr}`;

          // Chỉ xử lý nếu đã có đăng ký ăn trong ngày đó
          if (studentRecord[key] === "✓") {
            switch (item.sis_diemdanh_status_id) {
              case 3: // Vắng có phép
                studentRecord[key] = "P";
                studentRecord.nghi_phep++;
                break;
              case 4: // Vắng không phép
                studentRecord[key] = "KP";
                studentRecord.vang++;
                break;
            }
          }
        });

        // Đảm bảo điền “–” cho những ngày không có dữ liệu
        const transformed = Object.values(groupedData).map((student: any) => {
          student.di_hoc = 0;
          student.nghi_phep = 0;
          student.vang = 0;
          student.cas = 0;
          student.kas = 0;
          student.ka = 0;

          dayRange.forEach((dayStr) => {
            const key = `ngay_${dayStr}`;
            const value = student[key];

            if (!value || value === "–") {
              student[key] = "–";
              return;
            }

            switch (value) {
              case "✓":
                student.di_hoc++;
                const combo = student.comboByDay?.[dayStr];
                if (combo === 1) student.cas++;
                if (combo === 2) student.kas++;
                if (combo === 3) student.ka++;
                break;
              case "P":
                student.nghi_phep++;
                break;
              case "KP":
              case "V":
                student.vang++;
                break;
            }
          });

          student.ngay_tinh_phi = student.di_hoc + student.vang; // hoặc tùy quy định tính phí

          // Xác định tiểu học hay trung học
          const lopNumber = parseInt(student.ten_lop?.match(/\d+/)?.[0] || "0", 10);
          const isTieuHoc = lopNumber > 0 && lopNumber < 6;

          // Đơn giá
          const CAS_PRICE = isTieuHoc ? 70000 : 80000;
          const KAS_PRICE = isTieuHoc ? 55000 : 60000;

          // 👉 Thành tiền: tính theo ngày thực tế
          student.thanh_tien = student.cas * CAS_PRICE + student.kas * KAS_PRICE;

          // 👉 Dự thu: giữ cố định (không cần quan tâm ngày vắng)
          student.du_thu = isTieuHoc ? 1400000 : 1600000;

          // 👉 Format hiển thị
          student.thanh_tien_text = student.thanh_tien.toLocaleString("vi-VN") + " đ";
          student.du_thu_text = student.du_thu.toLocaleString("vi-VN") + " đ";


          return student;
        });

        transformed.sort((a: any, b: any) => {
          const tenKhoiA = a.ten_khoi || "";
          const tenKhoiB = b.ten_khoi || "";
          // 1. Khối
          if (tenKhoiA !== tenKhoiB) {
            return tenKhoiA.localeCompare(tenKhoiB, "vi", { numeric: true });
          }

          const tenLopA = a.ten_lop || "";
          const tenLopB = b.ten_lop || "";
          // 2. Lớp
          if (tenLopA !== tenLopB) {
            return tenLopA.localeCompare(tenLopB, "vi", { numeric: true });
          }

          // 3. Tên → Đệm → Họ
          const nameA = splitName(a.ho_ten);
          const nameB = splitName(b.ho_ten);

          // sort theo tên gọi (last) trước
          let cmp = nameA.last.localeCompare(nameB.last, "vi");
          if (cmp !== 0) return cmp;

          // nếu tên giống thì xét tên đệm
          cmp = nameA.middle.localeCompare(nameB.middle, "vi");
          if (cmp !== 0) return cmp;

          // cuối cùng mới xét họ
          return nameA.first.localeCompare(nameB.first, "vi");
        });


        setFoBaoCaoAns(transformed);
      }
    } finally {
      setLoading(false);
    }
  }, [dm_coso_id, dm_truong_id, nam_hoc, dataLoad, filterTruongId, filterDotDangKyId, filterStatusType, filterKhoiId, filterHeId]);

  const filteredData = useMemo(() => {
    return foBaoCaoAns.filter((item) => {
      if (filterStatusType === "dang_hoc") {
        if (!item.isDuTuyen && item.trang_thai_hoc_sinh !== "Đang học" && item.trang_thai !== "Đang học") {
          return false;
        }
      }
      return (
        (!filterDotDangKyId || item.fo_dotdangky_an_id === filterDotDangKyId) &&
        (!filterTruongId || item.dm_truong_id === filterTruongId) &&
        (!filterKhoiId || item.dm_khoi_id === filterKhoiId) &&
        (!filterHeId || item.dm_he_id === filterHeId)
      );
    });
  }, [foBaoCaoAns, filterDotDangKyId, filterTruongId, filterKhoiId, filterHeId, filterStatusType]);

  const generateColumns = useCallback((): IColumn[] => {
    const baseColumns: IColumn[] = [
      // { caption: "Mã KH", dataField: "ma_kh", isMainColumn: true, fixed: true, width: 120 },
      {
        caption: "Trạng thái",
        dataField: "trang_thai_hoc_sinh",
        isMainColumn: true,
        fixed: true,
        width: 110,
        align: "center",
        exportValue: (data: any) => data.trang_thai_hoc_sinh || data.trang_thai || "",
        cellRender: (data: any) => <span>{data.trang_thai_hoc_sinh || data.trang_thai || ""}</span>,
      },
      {
        caption: "Mã HS",
        dataField: "ma_hs",
        isMainColumn: true,
        fixed: true,
        width: 120,
        exportValue: (data: any) => data.ma_hs || "",
      },
      {
        dataField: "ho_ten",
        width: 200,
        caption: translate("Học sinh"),
        exportValue: (data: any) => data.ho_ten || "",
        cellRender: (data) => <HocSinhShortInfo data={data} />,
      },
      {
        caption: "Khối",
        dataField: "ten_khoi",
        align: "center",
        fixed: true,
        width: 50,
        exportValue: (data: any) => data.ten_khoi || "",
      },
      {
        caption: "Lớp",
        dataField: "ten_lop",
        align: "center",
        fixed: true,
        width: 50,
        exportValue: (data: any) => data.ten_lop || "",
      },
      {
        caption: "Ngày nhập học",
        dataField: "ngay_nhap_hoc",
        width: 110,
        minWidth: 110,
        align: "center",
        exportValue: (data: any) =>
          data.ngay_nhap_hoc ? dayjs(data.ngay_nhap_hoc).format("DD/MM/YYYY") : "",
        cellRender: (data: any) => (
          <span>
            {data.ngay_nhap_hoc ? dayjs(data.ngay_nhap_hoc).format("DD/MM/YYYY") : ""}
          </span>
        ),
      },
    ];

    const getDayColumns = (from: string, to: string): IColumn[] => {
      const fromDate = dayjs(from);
      const toDate = dayjs(to);
      const endOfMonth = fromDate.endOf("month");
      const validTo = toDate.isAfter(endOfMonth) ? endOfMonth : toDate;
      const result: IColumn[] = [];

      let current = fromDate;
      while (current.isSameOrBefore(validTo, "day")) {
        const dayStr = current.format("DD");
        const fieldKey = `ngay_${dayStr}`;

        result.push({
          caption: dayStr,
          dataField: fieldKey,
          width: 30,
          align: "center",
          cellRender: (data: any) => (
            <TextWithEllipse text={data[fieldKey] ?? "–"} lineNumber={1} />
          ),
        });

        current = current.add(1, "day");
      }

      return result;
    };

    const dynamicDateColumns = getDayColumns(dataLoad.from_date, dataLoad.to_date);

    const summaryColumns: IColumn[] = [
      { caption: "CAS", dataField: "cas", width: 30, align: "center" },
      { caption: "KAS", dataField: "kas", width: 30, align: "center" },
      { caption: "KA", dataField: "ka", width: 30, align: "center" },
      { caption: "Đi học", dataField: "di_hoc", width: 50, align: "center" },
      { caption: "Nghỉ phép", dataField: "nghi_phep", width: 50, align: "center" },
      { caption: "Vắng", dataField: "vang", width: 50, align: "center" },
      { caption: "Tính phí", dataField: "ngay_tinh_phi", width: 50, align: "center" },
      { caption: "Thành tiền", dataField: "thanh_tien_text", width: 100, align: "center" },
      { caption: "Dự thu", dataField: "du_thu_text", width: 100, align: "center" },
    ];

    return [...baseColumns, ...dynamicDateColumns, ...summaryColumns];

  }, [translate, dataLoad.from_date, dataLoad.to_date]);

  if (!canView) {
    return (
      <Box sx={{ textAlign: "center", padding: 4 }}>
        <Text text="Base.Message.NoPermission" />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end", mb: 3 }}>
        <Box>
          <MyText text="Từ ngày" sx={{ mb: 1, fontWeight: "600", fontSize: "13px", color: "fg.default", mr: 2 }} />
          <MyTextInput
            type="date"
            sx={{ width: "180px", height: "32px", borderRadius: "6px", border: "1px solid", borderColor: "border.default", "&:focus": { borderColor: "accent.emphasis", boxShadow: "0 0 0 2px rgba(3, 102, 214, 0.2)" } }}
            value={dataLoad.from_date}
            max={dataLoad.to_date}
            onChange={handleFromDateChange}
          />
        </Box>
        <Box>
          <MyText text="Đến ngày" sx={{ mb: 1, fontWeight: "600", fontSize: "13px", color: "fg.default", mr: 2 }} />
          <MyTextInput
            type="date"
            sx={{ width: "180px", height: "32px", borderRadius: "6px", border: "1px solid", borderColor: "border.default", "&:focus": { borderColor: "accent.emphasis", boxShadow: "0 0 0 2px rgba(3, 102, 214, 0.2)" } }}
            value={dataLoad.to_date}
            min={dataLoad.from_date}
            max={dayjs(dataLoad.from_date).endOf("month").format("YYYY-MM-DD")}
            onChange={handleToDateChange}
          />
        </Box>
        <Box>
          <ComboboxTruong value={filterTruongId} onValueChanged={(id) => { setFilterTruongId(id); setFilterKhoiId(0); setFilterHeId(0); }} isShowClearButton />
        </Box>
        <Box>
          <ComboboxKhoi value={filterKhoiId} onValueChanged={(id) => { setFilterKhoiId(id); setFilterHeId(0); }} dm_truong_id={filterTruongId || undefined} isShowClearButton />
        </Box>
        <Box>
          <ComboboxHe value={filterHeId} onValueChanged={setFilterHeId} dm_truong_id={filterTruongId || undefined} dm_khoi_id={filterKhoiId || undefined} isShowClearButton />
        </Box>
        <Box>
          <ComboboxDotDangKyAn value={filterDotDangKyId} onValueChanged={setFilterDotDangKyId} isShowClearButton isShowAll />
        </Box>
        <Box>
          <SelectBoxTrangThaiDangHoc
            value={filterStatusType}
            defaultValue="dang_hoc"
            onValueChanged={(val) => setFilterStatusType(val)}
            size="medium"
          />
        </Box>
        <Box>
          <Button text="Tổng hợp" leadingVisual={SearchIcon} isLoading={loading} onClick={handleSearchUserAsync} sx={{ height: "32px" }} />
        </Box>
      </Box>
      <DataTable
        data={filteredData}
        isLoading={loading || status === ePageBaseStatus.is_loading}
        title="Báo cáo ăn theo thời gian"
        height="calc(100vh - 240px)"
        exportEnable
        columns={generateColumns()}
        paging={{ enable: true, pageSizeItems: [50, 200, 500, 1000, 5000] }}
      />
    </Box>
  );
};

export default BaoCaoThangPage;